import * as React from 'react';
import type { Editor } from '@tiptap/react';

import type { CommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import { scrollToCommentThread } from './comment-navigation';
import { useCommentThreadActions } from './comment-thread-actions';
import { CommentThreadPanel } from './comment-thread-panel';
import { useCommentAnchorOffsets } from './comment-anchor-offsets';
import { useCommentOutsidePress } from './comment-outside-press';
import {
  useActiveCommentId,
  useCommentThreads,
  type CommentThreadSummary,
} from './comment-threads';

/** One fixed width, held for the comment column whatever the pane does. */
export const MARGIN_CARD_WIDTH_PX = 288;
export const MARGIN_CARD_GAP_PX = 16;

/** Vertical space between two cards that would otherwise overlap. */
const STACK_GAP_PX = 8;

interface Placement {
  thread: CommentThreadSummary;
  /** Where the anchored line is, before stacking. */
  anchorTop: number;
  /** Where the card actually goes, after stacking. */
  top: number;
}

/**
 * Lays the cards out down the margin: each beside its own line where it can be, pushed
 * down where the one above it would otherwise overlap.
 *
 * A single downward pass, so a card never moves up past its anchor — reading order and
 * document order stay the same, and a run of comments on neighbouring lines stacks
 * into a column rather than a pile.
 */
function stack(
  placements: Omit<Placement, 'top'>[],
  heights: Record<string, number>
): Placement[] {
  let floor = 0;
  return placements
    .slice()
    .sort((a, b) => a.anchorTop - b.anchorTop)
    .map((placement) => {
      const top = Math.max(placement.anchorTop, floor);
      floor = top + (heights[placement.thread.commentId] ?? 0) + STACK_GAP_PX;
      return { ...placement, top };
    });
}

export interface CommentMarginCardsProps {
  editor: Editor | null;
  author: CommentAuthor | null;
  /** The element the cards are positioned within — the editor's scroll container. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Where the card column starts, measured from the container's left edge. */
  left: number;
}

/**
 * Every open thread, as a card in the document's right margin.
 *
 * Shown by default wherever there is margin to show it in, because that is what the
 * margin is for: a comment on a document is a note against a line, and a reader should
 * not have to ask what has been said about the page they are reading. The rail answers
 * "what is outstanding here", which is a different question and still has its column.
 *
 * Only the thread the caret is in is expanded. The rest are capped and fade into
 * "... more", so a document with a dozen comments still reads as a document.
 */
export function CommentMarginCards({
  editor,
  author,
  containerRef,
  left,
}: CommentMarginCardsProps) {
  const { threads } = useCommentThreads(editor);
  const activeCommentId = useActiveCommentId(editor);
  const actions = useCommentThreadActions(editor, author);
  const [heights, setHeights] = React.useState<Record<string, number>>({});
  const cardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const heightObserverRef = React.useRef<ResizeObserver | null>(null);
  const layerRef = React.useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    setDismissed(false);
  }, [activeCommentId]);

  // Clicking away puts the column back the way it was found: every card capped, the
  // long ones ending in "... more". An expanded card left open over the margin of a
  // document nobody is reading comments on is just a card in the way.
  useCommentOutsidePress({
    // Stays armed once dismissed, so pressing the same span again can reopen it.
    enabled: activeCommentId !== null,
    isInside: (target) => layerRef.current?.contains(target) === true,
    onOutside: () => setDismissed(true),
    onCommentPress: () => setDismissed(false),
  });

  const expandedCommentId = dismissed ? null : activeCommentId;

  const open = React.useMemo(
    () => threads.filter((thread) => !thread.resolved),
    [threads]
  );

  const anchorTops = useCommentAnchorOffsets(editor, open, containerRef);

  // Heights come from the rendered cards, so the stack settles in a second pass: lay
  // them out at their anchors, measure, then push the overlaps down.
  //
  // Watched rather than measured each render. A card's height changes for reasons a
  // dependency list cannot name — a thread expanding, an edit box opening, a reply
  // wrapping onto another line, a font arriving — and measuring on every render to
  // catch them is a render that schedules a render.
  React.useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setHeights((previous) => {
        const next = { ...previous };
        let changed = false;
        for (const entry of entries) {
          const commentId = (entry.target as HTMLElement).dataset['commentId'];
          if (commentId === undefined) {
            continue;
          }
          const height =
            entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
          if (next[commentId] !== height) {
            next[commentId] = height;
            changed = true;
          }
        }
        return changed ? next : previous;
      });
    });
    heightObserverRef.current = observer;
    // Cards mounted before this ran — the first paint — are not watched yet.
    for (const node of Object.values(cardRefs.current)) {
      if (node != null) {
        observer.observe(node);
      }
    }
    return () => {
      observer.disconnect();
      heightObserverRef.current = null;
    };
  }, []);

  const placements = React.useMemo(
    () =>
      stack(
        open
          .filter((thread) => anchorTops[thread.commentId] !== undefined)
          .map((thread) => ({
            thread,
            anchorTop: anchorTops[thread.commentId] ?? 0,
          })),
        heights
      ),
    [anchorTops, heights, open]
  );

  if (author === null || placements.length === 0) {
    return null;
  }

  return (
    <div ref={layerRef} className="pointer-events-none absolute inset-0 z-10">
      {placements.map(({ thread, top }) => {
        const active = thread.commentId === expandedCommentId;
        return (
          <div
            key={thread.commentId}
            data-comment-id={thread.commentId}
            ref={(node) => {
              const observer = heightObserverRef.current;
              const previous = cardRefs.current[thread.commentId];
              if (previous != null) {
                observer?.unobserve(previous);
              }
              cardRefs.current[thread.commentId] = node;
              if (node !== null) {
                observer?.observe(node);
              }
            }}
            style={{
              top,
              left,
              width: MARGIN_CARD_WIDTH_PX,
              position: 'absolute',
            }}
            className="pointer-events-auto transition-[top] duration-150"
          >
            <CommentThreadPanel
              thread={thread}
              author={author}
              canEdit={editor?.isEditable === true}
              collapsed={!active}
              onExpand={() => {
                if (editor !== null) {
                  // Expanding is selecting: the caret moves into the thread, which is
                  // the one piece of state that says which card is open.
                  scrollToCommentThread(editor, thread.commentId);
                }
              }}
              onReply={(body) =>
                actions.addReply(thread.commentId, thread.replies, body)
              }
              onToggleResolved={() =>
                actions.toggleResolved(
                  thread.commentId,
                  thread.replies,
                  thread.resolved
                )
              }
              onEditReply={(replyId, body) =>
                actions.editReply({
                  commentId: thread.commentId,
                  replies: thread.replies,
                  resolved: thread.resolved,
                  replyId,
                  body,
                })
              }
              onDeleteReply={(replyId) =>
                actions.removeReply({
                  commentId: thread.commentId,
                  replies: thread.replies,
                  resolved: thread.resolved,
                  replyId,
                })
              }
            />
          </div>
        );
      })}
    </div>
  );
}
